// src/Pages/Catering.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  MenuItem,
  TextField,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fetchCateringOptions } from '../utils/googleApi';

const FORM_URL = process.env.REACT_APP_CATERING_FORM_URL || 'https://usebasin.com/f/e12735b1307b';

// Fallback options if no sheet is configured
const DEFAULT_PROTEINS = ['Smoked Brisket', 'Pork Butt', 'Spare Ribs'];
const DEFAULT_SIDES = ['Potato Salad', 'Mac & Cheese', 'Cream Corn'];

// Shared styles
const textFieldSx = {
  '& .MuiInputLabel-root': {
    fontFamily: 'Roboto, sans-serif',
    color: 'black',
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Roboto, sans-serif',
    color: 'black',
    fontSize: '16px',
  },
  '& .MuiOutlinedInput-root': {
    fontFamily: 'Roboto, sans-serif',
    color: 'black',
    '&.Mui-focused fieldset': {
      borderColor: '#333',
    },
  },
};

const sectionSx = {
  mb: 4,
  p: { xs: 2, md: 3 },
  backgroundColor: '#fff',
  borderRadius: 2,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  border: '1px solid #e0e0e0',
};

const sectionTitleSx = {
  fontFamily: 'Roboto, sans-serif',
  fontWeight: 'bold',
  color: 'black',
  mb: 2,
  fontSize: { xs: '1.1rem', md: '1.25rem' },
};

const checkboxLabelSx = {
  '& .MuiTypography-root': {
    fontFamily: 'Roboto, sans-serif',
    color: 'black',
    fontSize: { xs: '16px', md: '14px' },
  },
  '& .MuiCheckbox-root': {
    padding: { xs: '12px', md: '9px' },
  },
  mb: { xs: 1.5, md: 1 },
};

const navButtonSx = {
  fontFamily: 'Roboto, sans-serif',
  px: 4,
  py: 1.5,
  fontSize: '16px',
};

const steps = ['Your Info', 'Event Details', 'Package Selection'];

const PACKAGES = [
  {
    id: 'dropoff_hot',
    title: 'Drop-Off — Hot & Ready',
    description: 'Food is delivered hot and ready to eat. Perfect for events where you want zero setup — just open and serve.',
  },
  {
    id: 'dropoff_chafing',
    title: 'Drop-Off with Chafing Dishes',
    description: 'Delivered with disposable chafing dish setup so your guests can serve themselves when ready. Great for flexible timing.',
  },
  {
    id: 'full_service',
    title: 'Full-Service Catering',
    description: 'Our team attends your event to set up, serve, and break down. A fully hands-off experience for you and your guests.',
  },
];

const CateringPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Dynamic menu options
  const [proteinOptions, setProteinOptions] = useState(DEFAULT_PROTEINS);
  const [sideOptions, setSideOptions] = useState(DEFAULT_SIDES);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Address autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressDebounceRef = useRef(null);
  const addressBoxRef = useRef(null);

  useEffect(() => {
    const cateringSheetId = process.env.REACT_APP_GOOGLE_SHEET_CATERING_ID;
    const menuSheetId = process.env.REACT_APP_GOOGLE_SHEET_ID;

    fetchCateringOptions(cateringSheetId, menuSheetId)
      .then(data => {
        if (data) {
          if (data.proteins.length > 0) setProteinOptions(data.proteins);
          if (data.sides.length > 0) setSideOptions(data.sides);
        }
      })
      .catch(err => console.error('[LFBBQ] Catering options error:', err))
      .finally(() => setOptionsLoading(false));
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addressBoxRef.current && !addressBoxRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    guests: '',
    venueType: '',
    eventDate: '',
    eventTime: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    package: '',
    meats: [],
    sides: [],
    budget: '',
    notes: '',
  });

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Address field change — triggers autocomplete suggestions
  const handleAddressChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, address: value }));
    if (errors.address) {
      setErrors(prev => { const next = { ...prev }; delete next.address; return next; });
    }

    clearTimeout(addressDebounceRef.current);

    if (!value.trim() || value.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    addressDebounceRef.current = setTimeout(async () => {
      try {
        if (!window.google?.maps?.importLibrary) return;
        const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
        const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
          includedRegionCodes: ['us'],
        });
        setSuggestions(results || []);
        setShowSuggestions((results || []).length > 0);
      } catch (err) {
        console.error('[LFBBQ] Autocomplete suggestions error:', err);
      }
    }, 300);
  };

  // Called when user clicks a suggestion
  const handleSuggestionSelect = async (suggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);

    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents'] });

      let streetNumber = '';
      let route = '';
      let city = '';
      let state = '';
      let zip = '';

      (place.addressComponents || []).forEach(component => {
        const types = component.types;
        if (types.includes('street_number')) streetNumber = component.longText;
        if (types.includes('route'))          route        = component.shortText;
        if (types.includes('locality'))       city         = component.longText;
        if (types.includes('administrative_area_level_1')) state = component.shortText;
        if (types.includes('postal_code'))    zip          = component.longText;
      });

      setFormData(prev => ({
        ...prev,
        address: `${streetNumber} ${route}`.trim(),
        city,
        state,
        zip,
      }));

      setErrors(prev => {
        const next = { ...prev };
        delete next.address;
        delete next.city;
        delete next.state;
        delete next.zip;
        return next;
      });
    } catch (err) {
      console.error('[LFBBQ] Place details fetch error:', err);
      // Fallback: fill address with the suggestion text
      const text = suggestion.placePrediction?.text?.text || '';
      if (text) setFormData(prev => ({ ...prev, address: text }));
    }
  };

  const handleCheckbox = (field, value) => (e) => {
    setFormData(prev => {
      const arr = prev[field];
      if (e.target.checked) {
        return { ...prev, [field]: [...arr, value] };
      }
      return { ...prev, [field]: arr.filter(v => v !== value) };
    });
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.firstName.trim()) newErrors.firstName = 'Required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Required';
      if (!formData.email.trim()) newErrors.email = 'Required';
      if (!formData.guests) newErrors.guests = 'Required';
    }

    if (step === 1) {
      if (!formData.eventDate) newErrors.eventDate = 'Required';
      if (!formData.eventTime) newErrors.eventTime = 'Required';
      if (!formData.address.trim()) newErrors.address = 'Required';
      if (!formData.city.trim()) newErrors.city = 'Required';
      if (!formData.state.trim()) newErrors.state = 'Required';
      if (!formData.zip.trim()) newErrors.zip = 'Required';
    }

    if (step === 2) {
      if (!formData.package) newErrors.package = 'Please select a package';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => setActiveStep(prev => Math.max(prev - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const body = new FormData();
    body.append('firstName', formData.firstName);
    body.append('lastName', formData.lastName);
    body.append('email', formData.email);
    body.append('phone', formData.phone);
    body.append('guests', formData.guests);
    body.append('venueType', formData.venueType);
    body.append('eventDate', formData.eventDate);
    body.append('eventTime', formData.eventTime);
    body.append('address', formData.address);
    body.append('city', formData.city);
    body.append('state', formData.state);
    body.append('zip', formData.zip);
    body.append('package', formData.package);
    body.append('meats', formData.meats.join(', '));
    body.append('sides', formData.sides.join(', '));
    body.append('budget', formData.budget);
    body.append('notes', formData.notes);

    try {
      // Google Apps Script doesn't support CORS, so we use no-cors mode.
      // This means we can't read the response, but the request goes through
      // and the script receives + processes the data on Google's end.
      await fetch(FORM_URL, { method: 'POST', body, mode: 'no-cors' });
      setSubmitted(true);
    } catch (err) {
      console.error('Form submission error:', err);
      // Still show thank-you — if fetch threw here with no-cors it's a network issue
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{ fontFamily: '"Audiowide", sans-serif', color: 'black', mb: 2 }}
          >
            Thank You!
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontFamily: 'Roboto, sans-serif', color: 'black', fontSize: '18px' }}
          >
            Your catering request has been submitted. We'll be in touch soon!
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4, px: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Typography
        variant="h3"
        align="center"
        sx={{
          fontFamily: '"Audiowide", sans-serif',
          fontWeight: 'normal',
          color: 'black',
          mb: 3,
          fontSize: { xs: '1.75rem', md: '2.5rem' },
        }}
      >
        Catering Request
      </Typography>

      {/* Stepper */}
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          '& .MuiStepLabel-label': {
            fontFamily: 'Roboto, sans-serif',
            fontSize: { xs: '12px', md: '14px' },
          },
          '& .MuiStepIcon-root.Mui-active': { color: 'black' },
          '& .MuiStepIcon-root.Mui-completed': { color: 'black' },
        }}
      >
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box component="form" onSubmit={handleSubmit}>
        {/* Step 0: Your Info */}
        {activeStep === 0 && (
          <Box sx={sectionSx}>
            <Typography sx={sectionTitleSx}>Contact Information</Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="First Name"
                  required
                  fullWidth
                  value={formData.firstName}
                  onChange={handleChange('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  sx={textFieldSx}
                />
                <TextField
                  label="Last Name"
                  required
                  fullWidth
                  value={formData.lastName}
                  onChange={handleChange('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  sx={textFieldSx}
                />
              </Box>

              <TextField
                label="Email"
                type="email"
                required
                fullWidth
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                sx={textFieldSx}
              />

              <TextField
                label="Phone Number"
                type="tel"
                fullWidth
                value={formData.phone}
                onChange={handleChange('phone')}
                sx={textFieldSx}
              />

              <TextField
                label="Number of Guests"
                type="number"
                required
                fullWidth
                value={formData.guests}
                onChange={handleChange('guests')}
                error={!!errors.guests}
                helperText={errors.guests}
                inputProps={{ min: 1 }}
                sx={textFieldSx}
              />

              <TextField
                select
                label="Venue Type"
                fullWidth
                value={formData.venueType}
                onChange={handleChange('venueType')}
                sx={textFieldSx}
              >
                {[
                  'Catering Hall',
                  'Hotel',
                  'Park or Outdoor',
                  'Police or Fire Station',
                  'Private Home or Backyard',
                  'Private Office',
                  'Other',
                ].map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  ...navButtonSx,
                  backgroundColor: 'black',
                  color: 'white',
                  '&:hover': { backgroundColor: '#333' },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 1: Event Details */}
        {activeStep === 1 && (
          <Box sx={sectionSx}>
            <Typography sx={sectionTitleSx}>Date & Time</Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <TextField
                label="Event Date"
                type="date"
                required
                fullWidth
                value={formData.eventDate}
                onChange={handleChange('eventDate')}
                onInput={(e) => {
                  const val = e.target.value;
                  if (val && val !== formData.eventDate) {
                    setFormData(prev => ({ ...prev, eventDate: val }));
                  }
                }}
                error={!!errors.eventDate}
                helperText={errors.eventDate}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                sx={textFieldSx}
              />
              <TextField
                label="Event Time"
                type="time"
                required
                fullWidth
                value={formData.eventTime}
                onChange={handleChange('eventTime')}
                onInput={(e) => {
                  const val = e.target.value;
                  if (val && val !== formData.eventTime) {
                    setFormData(prev => ({ ...prev, eventTime: val }));
                  }
                }}
                error={!!errors.eventTime}
                helperText={errors.eventTime}
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Box>

            <Typography sx={sectionTitleSx}>Location</Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Address field with inline autocomplete dropdown */}
              <Box ref={addressBoxRef} sx={{ position: 'relative' }}>
                <TextField
                  label="Event Address"
                  required
                  fullWidth
                  value={formData.address}
                  onChange={handleAddressChange}
                  error={!!errors.address}
                  helperText={errors.address}
                  autoComplete="off"
                  sx={textFieldSx}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1300,
                      maxHeight: 240,
                      overflowY: 'auto',
                      borderRadius: 1,
                      mt: 0.5,
                    }}
                  >
                    <List dense disablePadding>
                      {suggestions.map((suggestion, idx) => (
                        <ListItem
                          key={idx}
                          onMouseDown={(e) => {
                            e.preventDefault(); // keep focus on field while clicking
                            handleSuggestionSelect(suggestion);
                          }}
                          sx={{
                            px: 2,
                            py: 1.25,
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                            borderBottom: idx < suggestions.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                          }}
                        >
                          <ListItemText
                            primary={suggestion.placePrediction?.mainText?.text}
                            secondary={suggestion.placePrediction?.secondaryText?.text}
                            primaryTypographyProps={{
                              fontFamily: 'Roboto, sans-serif',
                              fontSize: '14px',
                              color: 'black',
                              fontWeight: 500,
                            }}
                            secondaryTypographyProps={{
                              fontFamily: 'Roboto, sans-serif',
                              fontSize: '12px',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="City"
                  required
                  fullWidth
                  value={formData.city}
                  onChange={handleChange('city')}
                  error={!!errors.city}
                  helperText={errors.city}
                  sx={textFieldSx}
                />
                <TextField
                  label="State"
                  required
                  fullWidth
                  value={formData.state}
                  onChange={handleChange('state')}
                  error={!!errors.state}
                  helperText={errors.state}
                  sx={textFieldSx}
                />
              </Box>
              <TextField
                label="ZIP Code"
                required
                fullWidth
                value={formData.zip}
                onChange={handleChange('zip')}
                error={!!errors.zip}
                helperText={errors.zip}
                sx={textFieldSx}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  ...navButtonSx,
                  borderColor: 'black',
                  color: 'black',
                  '&:hover': { borderColor: '#333', backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  ...navButtonSx,
                  backgroundColor: 'black',
                  color: 'white',
                  '&:hover': { backgroundColor: '#333' },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Package Selection */}
        {activeStep === 2 && (
          <Box sx={sectionSx}>

            {/* $300 minimum disclaimer */}
            <Typography sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '12px',
              color: 'rgba(0,0,0,0.45)',
              textAlign: 'center',
              mb: 3,
              fontStyle: 'italic',
            }}>
              * Minimum catering request of $300
            </Typography>

            {/* Package Cards */}
            <Typography sx={sectionTitleSx}>Select a Package</Typography>
            {errors.package && (
              <Typography sx={{ color: '#d32f2f', fontSize: '12px', mb: 1, fontFamily: 'Roboto, sans-serif' }}>
                {errors.package}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {PACKAGES.map(pkg => {
                const selected = formData.package === pkg.id;
                return (
                  <Box
                    key={pkg.id}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, package: pkg.id }));
                      setErrors(prev => { const n = { ...prev }; delete n.package; return n; });
                    }}
                    sx={{
                      border: selected ? '2px solid #000' : '2px solid #e0e0e0',
                      borderRadius: 2,
                      p: { xs: 2, md: 2.5 },
                      cursor: 'pointer',
                      backgroundColor: selected ? 'rgba(0,0,0,0.03)' : '#fff',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      '&:hover': {
                        borderColor: '#000',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                      },
                    }}
                  >
                    <Box sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: selected ? 'none' : '2px solid #ccc',
                      backgroundColor: selected ? '#000' : 'transparent',
                      flexShrink: 0,
                      mt: '2px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {selected && (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'white' }} />
                      )}
                    </Box>
                    <Box>
                      <Typography sx={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 'bold',
                        fontSize: { xs: '15px', md: '16px' },
                        color: 'black',
                        mb: 0.5,
                      }}>
                        {pkg.title}
                      </Typography>
                      <Typography sx={{
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '13px',
                        color: 'rgba(0,0,0,0.6)',
                        lineHeight: 1.5,
                      }}>
                        {pkg.description}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Tell Us More */}
            <Divider sx={{ mb: 3 }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '11px', color: 'rgba(0,0,0,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                Tell Us More
              </Typography>
            </Divider>

            {optionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress sx={{ color: 'black' }} size={24} />
              </Box>
            ) : (
              <>
                <Typography sx={sectionTitleSx}>Protein Preferences</Typography>
                <FormGroup sx={{ mb: 3 }}>
                  {proteinOptions.map(meat => (
                    <FormControlLabel
                      key={meat}
                      control={
                        <Checkbox
                          checked={formData.meats.includes(meat)}
                          onChange={handleCheckbox('meats', meat)}
                          sx={{ '&.Mui-checked': { color: 'black' } }}
                        />
                      }
                      label={meat}
                      sx={checkboxLabelSx}
                    />
                  ))}
                </FormGroup>

                <Typography sx={sectionTitleSx}>Side Preferences</Typography>
                <FormGroup sx={{ mb: 3 }}>
                  {sideOptions.map(side => (
                    <FormControlLabel
                      key={side}
                      control={
                        <Checkbox
                          checked={formData.sides.includes(side)}
                          onChange={handleCheckbox('sides', side)}
                          sx={{ '&.Mui-checked': { color: 'black' } }}
                        />
                      }
                      label={side}
                      sx={checkboxLabelSx}
                    />
                  ))}
                </FormGroup>
              </>
            )}

            <TextField
              label="Estimated Budget"
              placeholder="e.g. $500 – $800"
              fullWidth
              value={formData.budget}
              onChange={handleChange('budget')}
              sx={{ ...textFieldSx, mb: 3 }}
            />

            <TextField
              label="Additional Notes"
              placeholder="Allergies, special requests, anything else we should know..."
              multiline
              rows={3}
              fullWidth
              value={formData.notes}
              onChange={handleChange('notes')}
              sx={{ ...textFieldSx, mb: 3 }}
            />

            {/* Summary preview */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: 'rgba(0,0,0,0.04)',
                '& .MuiAlert-icon': { color: 'black' },
              }}
            >
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px' }}>
                <strong>{formData.firstName} {formData.lastName}</strong> — {formData.guests} guests
                <br />
                {formData.eventDate} at {formData.eventTime}
                <br />
                {formData.address}, {formData.city}, {formData.state} {formData.zip}
                {formData.package && (
                  <><br /><strong>{PACKAGES.find(p => p.id === formData.package)?.title}</strong></>
                )}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                sx={{
                  ...navButtonSx,
                  borderColor: 'black',
                  color: 'black',
                  '&:hover': { borderColor: '#333', backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                Back
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  ...navButtonSx,
                  backgroundColor: 'black',
                  color: 'white',
                  '&:hover': { backgroundColor: '#333' },
                }}
              >
                Submit Request
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default CateringPage;
